import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, FormikProps } from 'formik';
import { inject, observer } from 'mobx-react';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Typography from '@material-ui/core/Typography';

import LoadingIndicator from 'components/Loading/LoadingIndicator';
import FormNumberField from 'components/FormFields/FormNumberField';
import Info from 'components/Tooltip/Info';
import { fractionToPercent, formatNumber, formatAmountValue } from 'helpers/helpers';
import withoutEnterSubmit from 'components/withoutEnterSubmit';

import { AssetCategory, BucketAssetClass, ClassesAsset } from 'common/interfaces';
import AssetDetail from './AssetDetail';
import useStyles from './currentPortfolioStyles';
import useOtherStyles from './otherStyles';
import { PortfolioEditorStore } from 'stores/workflowStores/newWorkflowPlanStore/portfolioEditorStore';
import { AssetChartStore } from 'stores/workflowStores/newWorkflowPlanStore/assetChartStore';
import { RootStore } from 'stores/rootStore';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  detailMode?: boolean;
  historyPage?: boolean;
  assetCategories: AssetCategory[] | undefined;
  terAverage?: number;
  distributionTotal?: number;
  editor?: boolean;
  store?: PortfolioEditorStore;
  rootStore?: RootStore;
  handleProcessing?: () => void;
  handleChecking?: (categories: AssetCategory[]) => void;
  processing?: boolean;
  reservedCashCode?: string;
  savingIsins?: string[];
}

const PortfolioTableCurrent: React.FunctionComponent<Props> = ({
  detailMode,
  historyPage,
  assetCategories,
  terAverage,
  distributionTotal,
  editor,
  store,
  rootStore,
  handleProcessing,
  handleChecking,
  processing,
  reservedCashCode,
  savingIsins,
}) => {
  const [expanded, setExpanded] = useState([] as string[]);
  const { t } = useTranslation();
  const cssClasses = useStyles();
  const otherClasses = useOtherStyles();

  const [chartStore] = useState(new AssetChartStore(rootStore!));

  useEffect(() => {
    if (store?.valid && store.editing) {
      handleProcessing && handleProcessing();
    }
  }, [store?.valid, store?.editing, handleProcessing]);

  const table = (formikProps?: FormikProps<any>) => {
    return assetCategories ? (
      <>
        <Table className={`${cssClasses.root}${detailMode ? '' : ' planTable'}${historyPage ? ' historyTable' : ''}`}>
          <TableHead>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell className={detailMode ? 'categoryHeader' : ''}>
                {editor ? (
                  <Typography variant={'h4'}>{t('portfolio_editor_selectedInstruments_title')}</Typography>
                ) : (
                  <>{t('accountPlan_investmentCategory')}</>
                )}
              </TableCell>
              {!editor && (
                <TableCell className={otherClasses.ter}>{t('accountPlan_portfolioTable_terTitle')}</TableCell>
              )}
              <TableCell
                className={`${otherClasses.shareTitle} ${
                  editor ? otherClasses.editShare : detailMode ? 'categoryShare' : otherClasses.overall
                }`}
              >
                {detailMode
                  ? t('accountPlan_targetCurrentShare')
                  : t(`accountPlan_targetShare${editor ? '_edit' : ''}`)}
              </TableCell>
              {detailMode && (
                <>
                  <TableCell className="categoryCurrent">{t('accountPlan_currentValue')}</TableCell>
                  <TableCell className="categoryProfit">
                    {t('accountPlan_currentProfit')}
                    <Info content={t('tooltip_plan_portfolioProfit')} inline />
                  </TableCell>
                </>
              )}
              {editor && <TableCell>{t('general_remove')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {assetCategories.map((assetCategory: AssetCategory, catKey: number) => {
              const currentShare =
                detailMode && assetCategory.current?.share !== undefined
                  ? fractionToPercent(assetCategory.current.share, 1, true)
                  : null;
              return (
                <React.Fragment key={assetCategory.code}>
                  <TableRow key={assetCategory.code} className={`${otherClasses.category} ${assetCategory.code}`}>
                    <TableCell>{processing && <LoadingIndicator size={24} color="inherit" />}</TableCell>
                    <TableCell scope="row" className="categoryTitle">
                      <strong>{t(assetCategory.title)}</strong>
                    </TableCell>
                    {!editor && <TableCell className={otherClasses.ter} />}
                    <TableCell align="right" className={`categoryShare ${!!currentShare ? '_detailMode' : ''}`}>
                      <strong style={{ opacity: store?.editing ? 0.25 : 1 }}>
                        {fractionToPercent(assetCategory.share, 1, !!currentShare)}
                        {currentShare && <> ({currentShare}) %</>}
                      </strong>
                    </TableCell>
                    {detailMode && <TableCell colSpan={3}> </TableCell>}
                    {editor && <TableCell> </TableCell>}
                  </TableRow>

                  {assetCategory.assetClasses.map((row: BucketAssetClass, key: number) => (
                    <React.Fragment key={`${row.code}_${key}`}>
                      {row.assets &&
                        row.assets.map((asset: ClassesAsset, i: number) => (
                          <React.Fragment key={asset.code}>
                            <TableRow
                              onClick={() => {
                                if (expanded.includes(asset.code)) {
                                  setExpanded(
                                    expanded.filter((childCode: string): boolean => childCode !== asset.code),
                                  );
                                } else {
                                  setExpanded([...expanded, asset.code]);
                                }
                              }}
                              className="_assetRow"
                            >
                              <TableCell scope="row">
                                <Button
                                  className={`${cssClasses.expandAction} ${
                                    expanded.includes(asset.code) ? cssClasses.expandActionExpanded : ''
                                  } button_${assetCategory.code}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                  }}
                                >
                                  <KeyboardArrowDownIcon />
                                </Button>
                              </TableCell>
                              <TableCell scope="row" className={'titleCell'}>
                                <strong>{asset.title}</strong>
                              </TableCell>
                              {!editor && (
                                <TableCell className={otherClasses.ter}>
                                  {typeof asset.ter === 'number' && fractionToPercent(asset.ter, 2)}
                                </TableCell>
                              )}
                              {editor && store && formikProps ? (
                                <>
                                  <TableCell scope="row" className={otherClasses.editShare}>
                                    <div>
                                      <TextField
                                        name={`assetCategories[${catKey}].assetClasses[${key}].assets[${i}].code`}
                                        type="hidden"
                                        style={{ display: 'none' }}
                                      />
                                      <TextField
                                        name={`assetCategories[${catKey}].assetClasses[${key}].assets[${i}].maxShare`}
                                        type="hidden"
                                        style={{ display: 'none' }}
                                      />
                                      <TextField
                                        name={`assetCategories[${catKey}].assetClasses[${key}].assets[${i}].varianceClass`}
                                        type="hidden"
                                        style={{ display: 'none' }}
                                      />
                                      <TextField
                                        name={`assetCategories[${catKey}].assetClasses[${key}].assets[${i}].isin`}
                                        type="hidden"
                                        style={{ display: 'none' }}
                                      />
                                      <FormNumberField
                                        //fast={false}
                                        name={`assetCategories[${catKey}].assetClasses[${key}].assets[${i}].share`}
                                        maxLength={4}
                                        decimalScale={1}
                                        afterChange={() => {
                                          store.setValid(false);
                                          store.setEditing(true);
                                          handleChecking && handleChecking(formikProps.values.assetCategories);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        endAdornment={'%'}
                                        error={
                                          // TODO: helper based on index
                                          formikProps?.values.assetCategories[catKey]?.assetClasses[key]?.assets[i]
                                            ?.share < 1 ||
                                          formikProps?.values.assetCategories[catKey]?.assetClasses[key]?.assets[i]
                                            ?.share > store.maxShare
                                          //(assetCategories[catKey].assetClasses[key].assets![i].maxShare || 0) * 100
                                        }
                                        disabled={asset.code === reservedCashCode}
                                      />
                                      {/*assetCategories[catKey].assetClasses[key].assets[i].isin*/}
                                      {/*asset.code !== reservedCashCode && (
                                        <span>
                                          (
                                          {fractionToPercent(
                                            assetCategories[catKey].assetClasses[key].assets![i].maxShare || 0,
                                            0,
                                            true,
                                          )}
                                          )
                                        </span>
                                      )*/}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {asset.code !== reservedCashCode && (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          store.removeFromSelectedAssets(catKey, key, i);
                                          store.check();
                                          e.stopPropagation();
                                        }}
                                        className={otherClasses.removeAsset}
                                      >
                                        <DeleteOutlineIcon />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </>
                              ) : (
                                <TableCell scope="row">
                                  {detailMode
                                    ? `${fractionToPercent(asset.share, 1, true)} ${
                                        asset.current?.share !== undefined
                                          ? ' (' + fractionToPercent(asset.current.share, 1, true) + ')'
                                          : ''
                                      } %`
                                    : fractionToPercent(asset.share)}
                                </TableCell>
                              )}
                              {detailMode && (
                                <>
                                  <TableCell scope="row">
                                    {asset.current?.value !== undefined
                                      ? `${formatNumber(asset.current.value, 2)} ${asset.current.currency}`
                                      : ''}
                                  </TableCell>
                                  <TableCell scope="row">
                                    {asset.current?.profit !== undefined
                                      ? formatAmountValue(asset.current.profit, asset.current.currency)
                                      : ''}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>

                            <TableRow key={asset.code}>
                              <TableCell
                                scope="row"
                                colSpan={detailMode ? 7 : editor ? 5 : 4}
                                className={otherClasses.assetDetailTd}
                              >
                                <Box
                                  className={`${otherClasses.assetDetail} ${
                                    expanded.includes(asset.code) ? otherClasses.assetShown : ''
                                  }`}
                                >
                                  <AssetDetail
                                    asset={asset}
                                    chartStore={chartStore}
                                    active={expanded.includes(asset.code)}
                                    savingIsins={savingIsins}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}
            {!editor && (
              <TableRow className={otherClasses.total}>
                <TableCell> </TableCell>
                <TableCell className={'titleCell'}>
                  <strong>{t('accountPlan_portfolioTable_total')}</strong>
                </TableCell>
                {!editor && (
                  <TableCell className={otherClasses.ter}>
                    {typeof terAverage === 'number' && fractionToPercent(terAverage, 2)}
                  </TableCell>
                )}
                <TableCell
                  className={
                    editor && store
                      ? store.sum === 100 || store.sum === null
                        ? otherClasses.correctSum
                        : otherClasses.incorrectSum
                      : ''
                  }
                >
                  <strong>
                    {store && store.sum !== null
                      ? `${store.sum} %`
                      : typeof distributionTotal === 'number' && fractionToPercent(distributionTotal)}
                  </strong>
                </TableCell>
                {detailMode && <TableCell colSpan={3}> </TableCell>}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </>
    ) : null;
  };

  return editor && assetCategories && store ? (
    <Formik
      initialValues={{ assetCategories: store.convertPortfolioData(assetCategories, true) }}
      enableReinitialize
      onSubmit={() => {}}
    >
      {(formikProps) => <FormWithoutEnterSubmit style={{ width: '100%' }}>{table(formikProps)}</FormWithoutEnterSubmit>}
    </Formik>
  ) : (
    table()
  );
};

export default inject('rootStore')(observer(PortfolioTableCurrent));
